/**
 * 非同期処理のユーティリティ関数
 */

import { logger } from '@/lib/logger';

/**
 * 指定時間待機する。AbortSignalによるキャンセルをサポート。
 */
export function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    const timeoutId = setTimeout(resolve, ms);

    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timeoutId);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true }
    );
  });
}

/**
 * リトライオプション
 */
export interface RetryOptions {
  /** 最大リトライ回数（デフォルト: 3） */
  maxRetries?: number;
  /** ベース遅延時間（ミリ秒、デフォルト: 1000） */
  baseDelay?: number;
  /** AbortSignal */
  signal?: AbortSignal;
  /** レート制限エラーかどうかを判定する関数 */
  isRateLimitError?: (error: Error) => boolean;
  /** Retry-After時間を取得する関数 */
  getRetryAfter?: (error: Error) => number | undefined;
  /** リトライ時のログ出力用コンテキスト名 */
  contextName?: string;
}

/**
 * エクスポネンシャルバックオフでリトライを行う
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    maxRetries = 3,
    baseDelay = 1000,
    signal,
    isRateLimitError,
    getRetryAfter,
    contextName = 'API',
  } = options;

  let lastError: Error | undefined;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    try {
      return await fn();
    } catch (error) {
      // AbortErrorはリトライせずに即座に投げる
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }

      lastError = error as Error;

      const rateLimitError = isRateLimitError?.(lastError) ?? false;

      if (rateLimitError) {
        const retryAfter = getRetryAfter?.(lastError);
        const waitTime = retryAfter || baseDelay * Math.pow(2, attempt);

        logger.warn(`${contextName}レート制限に到達`, {
          attempt: attempt + 1,
          maxRetries: maxRetries + 1,
          waitTime: `${(waitTime / 1000).toFixed(1)}秒`,
        });

        if (attempt < maxRetries) {
          await sleep(waitTime, signal);
          continue;
        } else {
          throw lastError;
        }
      }

      if (attempt < maxRetries) {
        logger.warn(`${contextName}呼び出しに失敗、リトライします`, {
          attempt: attempt + 1,
          error: lastError.message,
        });
        await sleep(baseDelay, signal);
      } else {
        throw lastError;
      }
    }
  }

  throw lastError!;
}
